/**
 * Merge shipment cartons and transfer orders into one sorted pre-pack queue.
 */

import type { CartonRow, TransferQueueRow } from "./prepackApi";

export type PrePackQueueEntry =
  | { kind: "shipment-carton"; rowKey: string; sortDate: string | null; carton: CartonRow }
  | { kind: "transfer"; rowKey: string; sortDate: string | null; transfer: TransferQueueRow };

function compareEntries(left: PrePackQueueEntry, right: PrePackQueueEntry): number {
  const lhsDate = String(left.sortDate ?? "9999-12-31");
  const rhsDate = String(right.sortDate ?? "9999-12-31");
  if (lhsDate < rhsDate) return -1;
  if (lhsDate > rhsDate) return 1;
  if (left.kind !== right.kind) {
    return left.kind === "shipment-carton" ? -1 : 1;
  }
  if (left.kind === "shipment-carton" && right.kind === "shipment-carton") {
    if (left.carton.shipmentId !== right.carton.shipmentId) {
      return left.carton.shipmentId - right.carton.shipmentId;
    }
    return left.carton.cartonNumber - right.carton.cartonNumber;
  }
  if (left.kind === "transfer" && right.kind === "transfer") {
    return left.transfer.transferOrderId - right.transfer.transferOrderId;
  }
  return 0;
}

export function mergePrePackQueue(cartons: CartonRow[], transfers: TransferQueueRow[]): PrePackQueueEntry[] {
  const entries: PrePackQueueEntry[] = [
    ...cartons.map(
      (carton): PrePackQueueEntry => ({
        kind: "shipment-carton",
        rowKey: carton.rowKey,
        sortDate: carton.mfrShipByDate,
        carton,
      }),
    ),
    ...transfers.map(
      (transfer): PrePackQueueEntry => ({
        kind: "transfer",
        rowKey: transfer.rowKey,
        sortDate: transfer.shipByDate,
        transfer,
      }),
    ),
  ];
  entries.sort(compareEntries);
  return entries;
}

export function entryNeedsWork(entry: PrePackQueueEntry): boolean {
  if (entry.kind === "shipment-carton") {
    return entry.carton.items.some((item) => item.pending > 0);
  }
  return !entry.transfer.isFullyPacked;
}

export function pickNextQueueEntry(entries: readonly PrePackQueueEntry[]): PrePackQueueEntry | null {
  return entries.find((entry) => entryNeedsWork(entry)) ?? null;
}

export function formatTransferRouteLabel(transfer: TransferQueueRow): string {
  const origin = transfer.originLocationAbbreviation ?? `Loc ${String(transfer.originLocationId)}`;
  const destination =
    transfer.destinationLocationAbbreviation ?? `Loc ${String(transfer.destinationLocationId)}`;
  return `${origin} → ${destination}`;
}
